import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Spinner } from '@fluxora/ui';

// E-SF18 promoted this helper into `bff-client` when the contract-review step
// needed the same parse. It is the SAME rule about the same header, so it is now
// read from one place rather than maintained in two: a divergence between the two
// copies would show up as one surface naming a downloaded file correctly and the
// other not, which nobody would connect back to a duplicated regex.
import { filenameFromDisposition } from '../api/bff-client';

/**
 * "Download your signed agreement" on the post-signature confirmation.
 *
 * ── WHY THIS IS A BUTTON AND NOT `<a href … download>` ─────────────────────
 * It was an anchor, and the anchor was silently wrong for this particular
 * endpoint. Two separate problems, one cause:
 *
 *   1. NO FEEDBACK. A shopper clicks, and the browser shows nothing at all
 *      until the bytes arrive. On a request that routinely takes over a
 *      minute (see below) that is indistinguishable from a dead button, so
 *      people click it repeatedly, each click starting another render.
 *   2. NO FAILURE PATH. When the request does fail, the browser owns the
 *      error — a native "site can't be reached" / failed-download chrome that
 *      the app cannot see, cannot explain, and cannot offer a retry from.
 *
 * A `fetch` gives us the pending state, the error, and the retry. The download
 * itself still happens natively, via an object URL, so the file lands in the
 * browser's downloads exactly as before.
 *
 * ── WHY THE FIRST ATTEMPT IS SLOW, AND WHY RETRY IS THE RIGHT AFFORDANCE ───
 * `GET /bff/status/:token/quote.pdf` proxies Quotation's canonical signed
 * agreement. That document is rendered LAZILY, on first download — the
 * accept-time render was removed because it was pushing `POST /bff/sign/accept`
 * past its gateway timeout. So the first reader of any given contract pays for
 * the whole 6-page rasterisation.
 *
 * Measured against deployed dev on 2026-08-19, same document back to back:
 *
 *     cold   no response after 120s (client gave up)
 *     warm   200 in 0.82s, 48,768 bytes
 *
 * The render COMPLETES server-side and is stored; it just outlives the BFF's
 * 30s ceiling on the hop, so the browser is handed a failure for work that
 * actually succeeded. That is why "it failed, I clicked again, it worked".
 *
 * ⚠️ THE CONSEQUENCE FOR COPY: a failure here is "not ready yet", NOT "broken".
 * Telling the shopper their agreement failed to download would be false — it
 * exists, it is being prepared, and trying again shortly genuinely works. The
 * error state says exactly that and keeps the retry in reach. Do not reword it
 * into a generic error without re-checking whether the lazy render is still
 * how this endpoint behaves.
 *
 * ⚠️ NOT A LOADER BUG TO "FIX" BY SHORTENING THE WAIT. The honest fix is
 * upstream: persist the agreement to real blob storage at sign time so the
 * cold path stops existing. Until then this component's job is to make the
 * wait legible rather than to hide it.
 */

/** After this long, tell the shopper it is still working rather than leaving a bare spinner. */
const SLOW_NOTICE_AFTER_MS = 8_000;

/** Used only if the response carries no usable `content-disposition` filename. */
const FALLBACK_FILENAME = 'rheora-signed-agreement.pdf';

type Phase = 'idle' | 'downloading' | 'error';

export interface SignedAgreementDownloadProps {
  /** Anonymous status token minted by accept; maps SERVER-SIDE to {tenantId, quoteId}. */
  readonly statusToken: string;
}

export function SignedAgreementDownload({
  statusToken,
}: SignedAgreementDownloadProps): JSX.Element {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('idle');
  const [slow, setSlow] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const clearSlowTimer = useCallback((): void => {
    if (slowTimerRef.current !== null) {
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
    }
  }, []);

  // Leaving the page mid-download must not leave a live request, a pending
  // timer, or a leaked object URL behind.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (slowTimerRef.current !== null) clearTimeout(slowTimerRef.current);
      if (objectUrlRef.current !== null) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const download = useCallback(async (): Promise<void> => {
    // Guard against a second click landing while the first is still in flight —
    // each one starts an independent render on a service that is already busy.
    if (phase === 'downloading') return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPhase('downloading');
    setSlow(false);
    clearSlowTimer();
    slowTimerRef.current = setTimeout(() => {
      setSlow(true);
    }, SLOW_NOTICE_AFTER_MS);

    try {
      const res = await fetch(`/bff/status/${encodeURIComponent(statusToken)}/quote.pdf`, {
        method: 'GET',
        // Same-origin, matching every other BFF call from this app.
        credentials: 'same-origin',
        headers: { accept: 'application/pdf' },
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`Agreement download failed with ${String(res.status)}`);
      }

      const blob = await res.blob();
      const filename =
        filenameFromDisposition(res.headers.get('content-disposition')) ?? FALLBACK_FILENAME;

      // Release the previous one before replacing the ref, or repeated
      // downloads accumulate blobs for the lifetime of the page.
      if (objectUrlRef.current !== null) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;

      // The native save. A detached anchor is the only way to hand the browser
      // a filename alongside blob bytes.
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      setPhase('idle');
    } catch (err) {
      // An abort is us tearing down, not a failure to report.
      if (controller.signal.aborted) return;
      setPhase('error');
    } finally {
      clearSlowTimer();
    }
  }, [clearSlowTimer, phase, statusToken]);

  const busy = phase === 'downloading';

  return (
    <span className="mt-1 flex flex-col gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-fit"
        onClick={() => {
          void download();
        }}
        disabled={busy}
        aria-busy={busy}
      >
        {busy ? t('shop.sign.confirm.downloadPreparing') : t('shop.sign.confirm.downloadPdf')}
      </Button>

      {/* One live region for both the pending and the slow notice, so a screen
          reader hears "preparing" once and then the escalation — not two
          competing regions announcing over each other. */}
      {busy ? (
        <span className="inline-flex items-center gap-2 text-xs text-muted" role="status">
          <Spinner size="sm" label={t('shop.sign.confirm.downloadPreparing')} />
          <span>
            {slow
              ? t('shop.sign.confirm.downloadSlow')
              : t('shop.sign.confirm.downloadPreparingHint')}
          </span>
        </span>
      ) : null}

      {phase === 'error' ? (
        <Alert variant="warning" title={t('shop.sign.confirm.downloadNotReady.title')} role="status">
          <span className="flex flex-col items-start gap-2">
            <span>{t('shop.sign.confirm.downloadNotReady.body')}</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                void download();
              }}
            >
              {t('shop.sign.confirm.downloadRetry')}
            </Button>
          </span>
        </Alert>
      ) : null}
    </span>
  );
}
